import { create } from 'zustand';

// T-234: Un "Flujo IA" es una secuencia EJECUTABLE de pasos. Cada paso declara
// su propio modelo ('' = default del usuario) y hasta 3 modelos asesores que
// responden primero y entregan su respuesta como insumo al modelo del paso.
//
// La EJECUCIÓN no vive aquí: la hace el orquestador de macros de EditorPage
// (T-231 / handleQuickAction('macro', flow, baseText)). Un flujo tiene el
// mismo shape { title, steps: [{ instruction, model, advisors }] } que un
// comando macro, así que EditorPage lo pasa DIRECTO como segundo argumento.
//
// Distinto de una Plantilla (comando con chatTemplate:true): la plantilla solo
// da estilo al chat, no ejecuta nada. Este store SOLO persiste la definición
// de los flujos (lista + CRUD); no sabe nada de créditos, ni de editor, ni de
// sesiones de chat. Mucho más simple que commandsStore: sin comandos de
// sistema, sin seed, sin categorías, sin chatTemplate.

/**
 * T-234: normaliza un flujo al shape { id, title, steps }.
 * Cada paso = { instruction: string, model: string, advisors: string[<=3] }.
 * Steps vacío/malformado → un único paso en blanco (mínimo 1 siempre).
 */
export const normalizeFlow = (flow) => {
  if (!flow || typeof flow !== 'object') {
    return { id: undefined, title: '', steps: [{ instruction: '', model: '', advisors: [] }] };
  }

  let steps;
  if (Array.isArray(flow.steps) && flow.steps.length > 0) {
    steps = flow.steps
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        instruction: typeof s.instruction === 'string' ? s.instruction : '',
        model: typeof s.model === 'string' ? s.model : '',
        advisors: Array.isArray(s.advisors)
          ? s.advisors.filter((a) => typeof a === 'string' && a.trim()).slice(0, 3)
          : []
      }));
  }
  if (!steps || steps.length === 0) {
    steps = [{ instruction: '', model: '', advisors: [] }];
  }

  return {
    id: flow.id,
    title: typeof flow.title === 'string' ? flow.title : '',
    steps
  };
};

// T-234: persistencia best-effort en localStorage (clave propia 'ai_flows').
const persistFlows = (flows) => {
  try {
    localStorage.setItem('ai_flows', JSON.stringify(flows));
  } catch (e) {
    // localStorage lleno o bloqueado: no rompemos el store por eso.
  }
};

export const useFlowsStore = create((set) => {
  let initialFlows = [];
  try {
    const stored = localStorage.getItem('ai_flows');
    initialFlows = stored ? JSON.parse(stored).map(normalizeFlow) : [];
  } catch (e) {
    initialFlows = [];
  }
  if (!Array.isArray(initialFlows)) initialFlows = [];

  return {
    flows: initialFlows,

    // T-234: alta — asigna id propio (Date.now) y normaliza antes de guardar.
    addFlow: (flow) => {
      set((state) => {
        const newFlow = normalizeFlow({ ...flow, id: Date.now().toString() });
        const updated = [...state.flows, newFlow];
        persistFlows(updated);
        return { flows: updated };
      });
    },

    // T-234: edición — mezcla updates sobre el flujo existente y re-normaliza.
    updateFlow: (id, updates) => {
      set((state) => {
        const updated = state.flows.map((f) =>
          f.id === id ? normalizeFlow({ ...f, ...updates, id }) : f
        );
        persistFlows(updated);
        return { flows: updated };
      });
    },

    // T-234: baja — filtra por id y persiste.
    deleteFlow: (id) => {
      set((state) => {
        const updated = state.flows.filter((f) => f.id !== id);
        persistFlows(updated);
        return { flows: updated };
      });
    }
  };
});
